import React from 'react';
import '../../styles/components/loading-states.css';

interface SpinnerProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'white';
  className?: string;
}

interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  className?: string;
}

interface SkeletonCardProps {
  lines?: number;
  className?: string;
}

interface SkeletonTableProps {
  rows?: number;
  columns?: number;
  className?: string;
}

interface LoadingOverlayProps {
  isLoading: boolean;
  children: React.ReactNode;
  message?: string;
  className?: string;
}

interface LoadingButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isLoading: boolean;
  children: React.ReactNode;
  loadingText?: string;
  disabled?: boolean;
  className?: string;
}

interface LoadingCardProps {
  isLoading: boolean;
  children: React.ReactNode;
  className?: string;
}

interface LoadingListProps {
  isLoading: boolean;
  children: React.ReactNode;
  itemCount?: number;
  className?: string;
}

interface LoadingModalProps {
  isLoading: boolean;
  children: React.ReactNode;
  className?: string;
}

interface LoadingPageProps {
  message?: string;
  className?: string;
}

interface LoadingWrapperProps {
  isLoading: boolean;
  loadingComponent?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

// Spinner Component
export function Spinner({ size = 'md', color = 'primary', className = '' }: SpinnerProps) {
  const sizeClasses: Record<string, string> = {
    xs: 'spinner-xs',
    sm: 'spinner-sm',
    md: 'spinner-md',
    lg: 'spinner-lg',
    xl: 'spinner-xl'
  };

  const colorClasses: Record<string, string> = {
    primary: 'spinner-primary',
    secondary: 'spinner-secondary',
    success: 'spinner-success',
    warning: 'spinner-warning',
    error: 'spinner-error',
    white: 'spinner-white'
  };

  return (
    <div className={`spinner ${sizeClasses[size]} ${colorClasses[color]} ${className}`}>
      <div className="spinner-inner"></div>
    </div>
  );
}

// Skeleton Loader
export function Skeleton({ width = '100%', height = '1rem', className = '' }: SkeletonProps) {
  return (
    <div 
      className={`skeleton ${className}`}
      style={{ width, height }}
    ></div>
  );
}

// Skeleton Card
export function SkeletonCard({ lines = 3, className = '' }: SkeletonCardProps) {
  return (
    <div className={`skeleton-card ${className}`}>
      <Skeleton height="1.5rem" width="60%" />
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton key={index} height="1rem" width={index === lines - 1 ? '40%' : '100%'} />
      ))}
    </div>
  );
}

// Skeleton Table
export function SkeletonTable({ rows = 5, columns = 4, className = '' }: SkeletonTableProps) {
  return (
    <div className={`skeleton-table ${className}`}>
      <div className="skeleton-table-header">
        {Array.from({ length: columns }).map((_, index) => (
          <Skeleton key={index} height="1rem" width="100%" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="skeleton-table-row">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton key={colIndex} height="1rem" width="100%" />
          ))}
        </div>
      ))}
    </div>
  );
}

// Loading Overlay
export function LoadingOverlay({ 
  isLoading, 
  children, 
  message = 'Yükleniyor...', 
  className = '' 
}: LoadingOverlayProps) {
  if (!isLoading) return <>{children}</>;

  return (
    <div className={`loading-overlay ${className}`}>
      {children}
      <div className="loading-overlay-content">
        <Spinner size="lg" color="white" />
        <p className="loading-message">{message}</p>
      </div>
    </div>
  );
}

// Loading Button
export function LoadingButton({ 
  isLoading, 
  children, 
  loadingText = 'Yükleniyor...', 
  disabled = false,
  className = '',
  ...props 
}: LoadingButtonProps) {
  return (
    <button 
      className={`loading-button ${isLoading ? 'loading' : ''} ${className}`}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <>
          <Spinner size="sm" color="white" />
          <span>{loadingText}</span>
        </>
      ) : (
        children
      )}
    </button>
  );
}

// Loading Card
export function LoadingCard({ 
  isLoading, 
  children, 
  className = '' 
}: LoadingCardProps) {
  return (
    <div className={`loading-card ${className}`}>
      {isLoading ? (
        <div className="loading-card-content">
          <Spinner size="md" />
          <p>İçerik yükleniyor...</p>
        </div>
      ) : (
        children
      )}
    </div>
  );
}

// Loading List
export function LoadingList({ 
  isLoading, 
  children, 
  itemCount = 5,
  className = '' 
}: LoadingListProps) {
  if (!isLoading) return <>{children}</>;

  return (
    <div className={`loading-list ${className}`}>
      {Array.from({ length: itemCount }).map((_, index) => (
        <div key={index} className="loading-list-item">
          <Skeleton height="1rem" width="70%" />
          <Skeleton height="0.875rem" width="50%" />
        </div>
      ))}
    </div>
  );
}

// Loading Modal
export function LoadingModal({ 
  isLoading, 
  children, 
  className = '' 
}: LoadingModalProps) {
  if (!isLoading) return <>{children}</>;

  return (
    <div className={`loading-modal ${className}`}>
      <div className="loading-modal-content">
        <Spinner size="lg" />
        <p>İşlem gerçekleştiriliyor...</p>
      </div>
    </div>
  );
}

// Loading Page
export function LoadingPage({ 
  message = 'Sayfa yükleniyor...', 
  className = '' 
}: LoadingPageProps) {
  return (
    <div className={`loading-page ${className}`}>
      <div className="loading-page-content">
        <Spinner size="xl" />
        <h2>{message}</h2>
        <p>Lütfen bekleyin...</p>
      </div>
    </div>
  );
}

// Loading States Hook
export function useLoadingState(initialState: boolean = false) {
  const [isLoading, setIsLoading] = React.useState<boolean>(initialState);
  
  const startLoading = React.useCallback(() => setIsLoading(true), []);
  const stopLoading = React.useCallback(() => setIsLoading(false), []);
  const toggleLoading = React.useCallback(() => setIsLoading(prev => !prev), []);
  
  return {
    isLoading,
    startLoading,
    stopLoading,
    toggleLoading,
    setIsLoading
  };
}

// Loading Wrapper
export function LoadingWrapper({ 
  isLoading, 
  loadingComponent, 
  children, 
  className = '' 
}: LoadingWrapperProps) {
  if (isLoading) {
    return <>{loadingComponent || <LoadingPage />}</>;
  }
  
  return <div className={className}>{children}</div>;
}

// Data Loading Component (for table/list loading states)
interface DataLoadingProps {
  message?: string;
  className?: string;
  type?: 'table' | 'list' | 'cards' | string;
}

export function DataLoading({ message = 'Veriler yükleniyor...', className = '', type }: DataLoadingProps) {
  return (
    <div className={`data-loading data-loading-${type || 'default'} ${className}`}>
      <Spinner size="md" />
      <p className="data-loading-message">{message}</p>
    </div>
  );
}

export default {
  Spinner,
  Skeleton,
  SkeletonCard,
  SkeletonTable,
  LoadingOverlay,
  LoadingButton,
  LoadingCard,
  LoadingList,
  LoadingModal,
  LoadingPage,
  useLoadingState,
  LoadingWrapper,
  DataLoading
};

